<template>
  <div>
    <app-cells v-if="!reservation.supplier_invoice">
      <app-button @click.native="$modal.show('invoice_modal')">Attach Invoice</app-button>
    </app-cells>
    <v-client-table
      v-else
      :data="[reservation.supplier_invoice]"
      :columns="table.columns"
      :options="table.options"
      class="table-default table-default--indent"
    >
      <template #h__supplier>
        <div class="table-default__left">Supplier</div>
      </template>
      <template #h__responsible>
        <div class="table-default__left">Responsible</div>
      </template>
      <template #date="{ row }">{{ dateFormat(row.date) }}</template>
      <template #supplier="{ row }">
        <div class="table-default__left">{{ row.supplier.legal_name }}</div>
      </template>
      <template #responsible="{ row }">
        <div class="table-default__left">{{ row.responsible?.full_name || '-' }}</div>
      </template>
      <template #amount="{ row }">
        {{ amountFormat(row.amount_total) }} {{ row.currency.code }}
      </template>
      <template #invoice_number="{ row }">{{ row.invoice_number || '-' }}</template>
      <template #account="{ row }">
        <template v-if="row.supplier_payment_method?.payment_method.code === 'bank'">
          <template v-if="row.supplier_payment_method.bank_account">
            {{
              row.supplier_payment_method.bank_account.account_number
            }}<br>{{ row.supplier_payment_method.bank_account.bank.short_name }}
          </template>
          <template v-else>-</template>
        </template>
        <template v-else-if="row.supplier_payment_method?.payment_method.code === 'mobile'">
          <template v-if="row.supplier_payment_method.mobile_account">
            {{ row.supplier_payment_method.mobile_account.account_number
            }}<br>{{ row.supplier_payment_method.mobile_account.provider.name }}
          </template>
          <template v-else>-</template>
        </template>
        <template v-else-if="row.supplier_payment_method?.payment_method.code === 'gepg'">
          <template v-if="row.gepg_number">{{ row.gepg_number }}</template>
          <template v-else>-</template>
        </template>
        <template v-else>-</template>
      </template>
      <template #payment_plans="{ row }">
          <span
            :style="{ 'backgroundColor': row.payment_status === 'unpaid' ? '#e85c20' : row.payment_status === 'paid' ? '#77b017' : '#439ad1' }"
            class="status-tag"
          >
            {{ row.payment_status === 'unpaid' ? 'Unpaid' : row.payment_status === 'paid' ? 'Paid' : amountFormat(row.paid_amount)
            }}
          </span>
      </template>
    </v-client-table>
    <app-flex-grid :rows="[['150px', '150px', '150px', '150px', '150px'], ['150px', '150px', '150px']]"
                   alignItems="end">
      <template #col-1-1>
        <app-form-group v-if="reservation.supplier_invoice" label="Invoice amount" label-for="invoice_amount_supplier"
                        class="form-group--no-indent" required>
          <app-fin-number
            v-model="form_supplier_invoice.amount_total"
            id="invoice_amount_supplier"
            :error="$v.form_supplier_invoice.amount_total.$error"
          />
        </app-form-group>
      </template>
      <template #col-1-2>
        <app-form-group v-if="reservation.supplier_invoice" label="Invoice date" class="form-group--no-indent" required>
          <app-date-picker
            v-model="form_supplier_invoice.date"
            :clearable="false"
            placeholder="Invoice date"
            :error="$v.form_supplier_invoice.date.$error"
          />
        </app-form-group>
      </template>
      <template #col-1-3>
        <app-form-group v-if="reservation.supplier_invoice" label="Invoice number" label-for="invoice_number_supplier"
                        class="form-group--no-indent" required>
          <app-input
            v-model="form_supplier_invoice.invoice_number"
            placeholder="Invoice number"
            id="invoice_number_supplier"
            :error="$v.form_supplier_invoice.invoice_number.$error"
          />
        </app-form-group>
      </template>
      <template #col-1-4>
        <app-form-group v-if="reservation.supplier_invoice" label="Attachment" class="form-group--no-indent" required>
          <app-uploader
            v-model="form_supplier_invoice.attachment"
            :extensions="['application/pdf', 'image/jpeg', 'image/png']"
            formDataFormats="PDF,JPEG,PNG"
            class="custom-uploader--flex"
          />
        </app-form-group>
      </template>
      <template #col-1-5>
        <app-button v-if="reservation.supplier_invoice" @click.native="onCheckInvoiceSupplierForm"
                    ref="submit_supplier_invoice">Save
        </app-button>
      </template>
      <template #col-2-1>
        <app-form-group label="Estimated price" class="form-group--no-indent">
          <template v-if="reservation.estimated_price === 0" #additional>
            <img
              src="@/assets/img/warning-icon.svg"
              alt="Confirmed"
              v-tooltip="{ content: 'Estimated price not found! Contact your admin', placement: 'top', offset: 10 }"
            >
          </template>
          <app-input :value="`${reservation.estimated_price.toLocaleString()} USD`" readonly/>
        </app-form-group>
      </template>
      <template #col-2-2>
        <app-form-group v-if="reservation.fees && reservation.fees > 0" label="Fees" class="form-group--no-indent">
          <app-input :value="`${reservation.fees.toLocaleString()} USD`" readonly/>
        </app-form-group>
      </template>
      <template #col-2-3>
        <app-form-group label="Calculated" label-for="calculated" class="form-group--no-indent" required>
          <app-input
            v-if="reservation.estimated_price_datetime"
            :value="dateTimeFormat(reservation.estimated_price_datetime)"
            placeholder="Calculated"
            id="calculated"
            readonly
          />
          <app-input
            v-else
            value="N/A"
            placeholder="Calculated"
            id="calculated"
            readonly
          />
        </app-form-group>
      </template>
    </app-flex-grid>
    <template v-if="reservation.supplier_invoice">
      <app-cells><h2 class="title">Payment plan</h2></app-cells>
      <v-client-table
        :data="reservation.supplier_invoice.payment_plans"
        :columns="table_payment_plan.columns"
        :options="table_payment_plan.options"
        class="table-default table-default--40 table-default--indent"
      >
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #amount="{ row }">{{ amountFormat(row.amount) }}</template>
        <template #paid="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.paid ? '#77b017' : '#e85c20' }"
          >
            {{ row.paid ? 'Yes' : 'No' }}
          </span>
        </template>
        <template #request="{ row }">
          <router-link
            v-if="row.payment_request"
            :to="{ name: 'reservation-payment-requests-detail', params: { id: row.payment_request } }"
            class="table-default__link-border table-default__link-border--center"
          >
            #{{ row.payment_request }}
          </router-link>
          <template v-else>-</template>
        </template>
      </v-client-table>
    </template>
    <app-cells>
      <h2 class="title">Payment policy</h2>
      <div v-if="!reservation.hotel.payment_policy" class="status-tag" style="background-color: #e85c20">
        No info about payment policy
      </div>
      <div
        v-else-if="reservation.hotel.payment_policy.code === 'check_out'"
        class="status-tag"
        style="background-color: #439ad1"
      >
        After check-out
      </div>
      <div
        v-else-if="reservation.hotel.payment_policy.code === 'immediate'"
        class="status-tag"
        style="background-color: #77b017"
      >
        Immediate ({{ reservation.hotel.payment_immediate_percentage }}%)
      </div>
      <div
        v-else-if="reservation.hotel.payment_policy.code === 'standard'"
        class="status-tag"
        style="background-color: #77b017"
      >
        Standard
      </div>
    </app-cells>
    <v-client-table
      v-if="reservation.hotel.payment_policy_terms && reservation.hotel.payment_policy_terms.length"
      :data="reservation.hotel.payment_policy_terms"
      :columns="table_payment_policy_terms.columns"
      :options="table_payment_policy_terms.options"
      class="table-default table-default--40"
    />
    <app-page-container v-if="reservation.abnormal_price.abnormal_price" extra>
      <h2 class="title title--mb15 title--mt30">Abnormal price verification</h2>
      <app-form-group label="Hotel database manager note">
        <app-textarea
          v-model="reservation.abnormal_price.notes"
          rows="5"
          @change.native="onUpdateAbnormalData({ notes: reservation.abnormal_price.notes })"
          placeholder="Hotel database manager note"
          :readonly="!is_general_manager && !is_reservation_supervisor"
        />
      </app-form-group>
      <div class="downloads">
        <div class="downloads__item" :class="{'downloads__item--loading': abnormal_loader}">
          <div class="downloads__left">
            <div class="downloads__info">
              <span class="downloads__name downloads__name--no-events">Completed</span>
            </div>
          </div>
          <div class="downloads__right">
            <div class="downloads__values">
              <span class="downloads__key">Status:</span>
              <app-switcher
                v-model="reservation.abnormal_price.verified"
                @change="onUpdateAbnormalData({ verified: reservation.abnormal_price.verified }, true)"
                :readonly="!is_general_manager"
              />
            </div>
            <template v-if="reservation.abnormal_price.verified && !abnormal_loader">
              <div class="downloads__values">
                <span class="downloads__key">Verified at:</span>
                <span class="downloads__value downloads__width">
                  {{ dateTimeFormat(reservation.abnormal_price.verified_at) }}
                </span>
              </div>
              <div class="downloads__values">
                <span class="downloads__key">Verified by:</span>
                <div class="downloads__person downloads__width">
                  <div class="downloads__photo">
                    <img :src="getUserAvatar(reservation.abnormal_price.verified_by?.crop.crop_url)" alt="Person">
                  </div>
                  <span class="downloads__value">{{ reservation.abnormal_price.verified_by?.full_name || '-' }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </app-page-container>
    <modal
      name="invoice_modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('invoice_modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Attach Invoice</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Invoice date" required>
              <app-date-picker
                v-model="form.date"
                :clearable="false"
                placeholder="Invoice date"
                :error="$v.form.date.$error"
              />
              <template #error>
                <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Invoice number" label-for="invoice_number" required>
              <app-input
                v-model="form.invoice_number"
                placeholder="Invoice number"
                id="invoice_number"
                :error="$v.form.invoice_number.$error"
              />
              <template #error>
                <div v-if="$v.form.invoice_number.$dirty && !$v.form.invoice_number.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Invoice amount" label-for="invoice_amount" required>
              <app-fin-number
                v-model="form.amount_total"
                id="invoice_amount"
                :error="$v.form.amount_total.$error"
              />
              <template #error>
                <div v-if="$v.form.amount_total.$dirty && !$v.form.amount_total.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Attachment" required>
              <app-uploader
                v-model="form.attachment"
                :extensions="['application/pdf', 'image/jpeg', 'image/png']"
                formDataFormats="PDF,JPEG,PNG"
                class="custom-uploader--flex"
              />
              <template #error>
                <div v-if="$v.form.attachment.$dirty && !$v.form.attachment.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_invoice">Attach</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  patchReservationsHotelsAbnormalPriceVerification,
  patchReservationsHotelsInvoice,
  postReservationsHotelsInvoice
} from '@/http'
import { format, parseISO } from 'date-fns'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'ReservationHotelsFinance',
  props: {
    reservation: {
      type: Object,
    },
  },
  data() {
    return {
      table_payment_plan: {
        items: [],
        columns: ['date', 'amount', 'paid', 'request'],
        options: {
          headings: {
            date: 'Date',
            amount: 'Amount',
            paid: 'Paid',
            request: 'Request',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table: {
        items: [],
        columns: [
          'date',
          'supplier',
          'amount',
          'invoice_number',
          'responsible',
          'account',
          'payment_plans',
        ],
        options: {
          headings: {
            'date': 'Date',
            'supplier': 'Supplier',
            'amount': 'Amount',
            'invoice_number': 'Invoice number',
            'responsible': 'Responsible',
            'account': 'Account',
            'payment_plans': 'Payment',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 1,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      form: {},
      form_supplier_invoice: {},
      table_payment_policy_terms: {
        items: [],
        columns: ['days', 'percentage'],
        options: {
          headings: {
            days: 'Days',
            percentage: '%',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      abnormal_loader: false,
    }
  },
  validations: {
    form: {
      date: { required },
      invoice_number: { required },
      amount_total: { required },
      attachment: { required },
    },
    form_supplier_invoice: {
      date: { required },
      invoice_number: { required },
      amount_total: { required },
      attachment: { required },
    }
  },
  watch: {
    'reservation.supplier_invoice': {
      handler(val) {
        if (val?.amount_total) {
          this.form_supplier_invoice = {
            amount_total: val.amount_total,
            attachment: val.attachment,
            invoice_number: val.invoice_number,
            date: val.date,
          }
        }
      },
      immediate: true,
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_reservation_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('reservation_supervisor')
    },
  },
  methods: {
    onUpdateAbnormalData(data, loader = false) {
      if (loader) this.abnormal_loader = true
      patchReservationsHotelsAbnormalPriceVerification(this.$route.params.id, data)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Change saved',
          })
          this.$emit('get-reservation-data')
          if (loader) this.abnormal_loader = false
        })
        .catch(() => { if (loader) this.abnormal_loader = false })
    },
    onCheckInvoiceSupplierForm() {
      this.$v.form_supplier_invoice.$touch()
      if (this.$v.form_supplier_invoice.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendInvoiceSupplierForm()
      }
    },
    sendInvoiceSupplierForm() {
      this.$refs.submit_supplier_invoice.preload = true
      patchReservationsHotelsInvoice(
        this.$route.params.id,
        this.reservation.supplier_invoice.id,
        { ...this.form_supplier_invoice, attachment: this.form_supplier_invoice.attachment.id }
      )
        .then(() => {
          this.$emit('get-reservation-data')
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$refs.submit_supplier_invoice.preload = false
        })
        .catch(() => {
          this.$refs.submit_supplier_invoice.preload = false
        })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit_invoice.preload = true
      postReservationsHotelsInvoice(this.$route.params.id, { ...this.form, attachment: this.form.attachment.id })
        .then(() => {
          this.$emit('get-reservation-data')
          this.$notify({
            type: 'success',
            title: 'Invoice created',
          })
          this.$modal.hide('invoice_modal')

        })
        .catch(() => {
          this.$refs.submit_invoice.preload = false
        })
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>